import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Paper } from '@esgian/esgianui';
import { ProfileLink } from '@components/Links';
import { dateSortCompare, getConvertTimeToHours, getDirection } from '@helpers';
import { useSelector } from 'react-redux';
import { getUser } from '@store/features';
import { GEOGRAPHICAL_TYPES } from '@constants';
import { useTimezone } from '@hooks/useTimezone';
import TableV2 from '@components/Tables/TableV2';
import DownloadDateRangeModal from '@components/Inputs/DownloadDateRangeModal/DownloadDateRangeModal';
import { useParams } from 'react-router-dom';
import { convertCanalDeparturesData } from '@api';

// Original getTimeDisplay definition

function CanalVesselPortCallsTable({
  title = 'Recent Departures',
  id,
  loading = false,
  canalDeparturesData = [],
  fileName = '',
  hideColumns = [],
  hasExportPeriodMenu = false
}) {
  const user = useSelector(getUser);
  const [showModal, setShowModal] = useState(false);
  const { getTimeZoneDisplay } = useTimezone();
  const params = useParams();

  // Transform incoming data
  const tableData = useMemo(() => {
    if (!canalDeparturesData?.length) return [];
    return convertCanalDeparturesData(canalDeparturesData);
  }, [canalDeparturesData]);
  // All column definitions
  const allColumns = [
    {
      name: 'vessel',
      label: 'Vessel',
      options: {
        sortCompare: (order) => (obj1, obj2) => {
          let val1 = obj1.data ? obj1.data.name : '-';
          let val2 = obj2.data ? obj2.data.name : '-';
          return order === 'asc' ? (val2 <= val1 ? 1 : -1) : val2 >= val1 ? 1 : -1;
        },
        customDownloadValue: (value) => {
          return value?.name ?? '-';
        },
        filterType: 'multiselect',
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value) => {
          return value ? <ProfileLink profile="vessel" name={value.name} id={value.id} /> : '-';
        }
      }
    },
    {
      name: 'imo',
      label: 'IMO Number',
      options: {
        filterType: 'multiselect',
        sort: true,
        sortThirdClickReset: true,
        display: title === 'In Transit' ? false : true
      }
    },
    {
      name: 'operator',
      label: 'Operator',
      options: {
        sort: true,
        filterType: 'multiselect',
        customDownloadValue: (value) => {
          return value?.name ?? '-';
        },
        sortCompare: (order) => (obj1, obj2) => {
          let val1 = obj1.data ? obj1.data.name : '-';
          let val2 = obj2.data ? obj2.data.name : '-';
          return order === 'asc' ? (val2 <= val1 ? 1 : -1) : val2 >= val1 ? 1 : -1;
        },
        sortThirdClickReset: true,
        customBodyRender: (value) => {
          return value ? <ProfileLink profile="operator" name={value.name} id={value.id} /> : '-';
        }
      }
    },
    {
      name: 'speed',
      label: 'Speed',
      options: {
        sort: true,
        sortThirdClickReset: true,
        filterType: 'multiselect'
      }
    },
    {
      name: 'heading',
      label: 'Heading',
      options: {
        sort: true,
        sortThirdClickReset: true,
        filterType: 'multiselect'
      }
    },
    {
      name: 'canalArrival',
      label: 'Arrival',
      options: {
        sort: true,
        sortThirdClickReset: true,
        customDownloadValue: (value) => value,
        sortCompare: (order) => (obj1, obj2) => {
          return dateSortCompare(obj1, obj2, order);
        },
        customBodyRender: (value) => {
          return value ? getTimeZoneDisplay(value) : '-';
        }
      }
    },
    {
      name: 'canalDeparture',
      label: 'Departure',
      options: {
        sort: true,
        sortThirdClickReset: true,
        customDownloadValue: (value) => value,
        sortCompare: (order) => (obj1, obj2) => {
          return dateSortCompare(obj1, obj2, order);
        },
        customBodyRender: (value) => {
          return value ? getTimeZoneDisplay(value) : '-';
        }
      }
    },
    {
      name: 'transit',
      label: 'Time in transit (hours)',
      options: {
        sort: true,
        sortThirdClickReset: true,
        sortCompare: (order) => (obj1, obj2) => {
          let val1 = getConvertTimeToHours(obj1.data);
          let val2 = getConvertTimeToHours(obj2.data);
          return order === 'asc' ? val1 - val2 : val2 - val1;
        },
        customBodyRender: (value) => {
          return getConvertTimeToHours(value);
        }
      }
    },
    {
      name: 'headingDirection',
      label: 'Direction',
      options: {
        sort: true,
        sortThirdClickReset: true,
        sortCompare: (order) => (obj1, obj2) => {
          let val1 = getDirection(obj1.data);
          let val2 = getDirection(obj2.data);
          const comparison = val1.localeCompare(val2);
          return order === 'asc' ? comparison : -comparison;
        },
        customBodyRender: (value) => {
          return value !== null ? getDirection(value) : '-';
        },
        filterType: 'multiselect'
      }
    },
    {
      name: 'originPort',
      label: 'Origin',
      options: {
        sort: true,
        sortThirdClickReset: true,
        sortCompare: (order) => (obj1, obj2) => {
          let val1 = obj1.data?.name ?? '';
          let val2 = obj2.data?.name ?? '';
          const comparison = val1.localeCompare(val2);
          return order === 'asc' ? comparison : -comparison;
        },
        customDownloadValue: (value) => {
          return value?.name ?? '-';
        },
        customBodyRender: (value) => {
          return value ? <ProfileLink profile="port" name={value.name} id={value.id} /> : '-';
        }
      }
    },
    {
      name: 'originATD',
      label: 'Origin ATD',
      options: {
        sort: true,
        sortThirdClickReset: true,
        sortCompare: (order) => (obj1, obj2) => {
          return dateSortCompare(obj1, obj2, order);
        },
        customBodyRender: (value) => {
          return value ? getTimeZoneDisplay(value) : '-';
        }
      }
    },
    {
      name: 'canalArrival',
      label: 'Canal Arrival',
      options: {
        sort: true,
        sortThirdClickReset: true,
        sortCompare: (order) => (obj1, obj2) => {
          return dateSortCompare(obj1, obj2, order);
        },
        customBodyRender: (value) => {
          return value ? getTimeZoneDisplay(value) : '-';
        }
      }
    },
    {
      name: 'destination',
      label: 'Destination',
      options: {
        sort: true,
        sortThirdClickReset: true,
        filterType: 'multiselect',
        sortCompare: (order) => (obj1, obj2) => {
          const val1 = obj1.data.name ?? '';
          const val2 = obj2.data.name ?? '';

          const comparison = val1.localeCompare(val2);
          return order === 'asc' ? comparison : -comparison;
        },
        customDownloadValue: (value) => {
          return value?.name ?? '-';
        },
        customBodyRender: (value) => {
          if (value?.name && value?.id) {
            return <ProfileLink profile="port" id={value?.id} name={value?.name} />;
          } else if (value?.name && !value?.id) {
            return value.name;
          }
          return '-';
        }
      }
    },
    {
      name: 'destinationDateETA',
      label: 'Destination ETA',
      options: {
        sort: true,
        sortThirdClickReset: true,
        customDownloadValue: (value) => value,
        sortCompare: (order) => (obj1, obj2) => {
          return dateSortCompare(obj1, obj2, order);
        },
        customBodyRender: (value) => {
          return value ? getTimeZoneDisplay(value) : '-';
        }
      }
    },
    {
      name: 'destinationDateATA',
      label: 'Destination ATA',
      options: {
        sort: true,
        sortThirdClickReset: true,
        customDownloadValue: (value) => value,

        sortCompare: (order) => (obj1, obj2) => {
          return dateSortCompare(obj1, obj2, order);
        },
        customBodyRender: (value) => {
          return value ? getTimeZoneDisplay(value) : '-';
        }
      }
    },
    {
      name: 'eta',
      label: 'ETA',
      options: {
        sort: true,
        sortThirdClickReset: true,
        customDownloadValue: (value) => value,
        sortCompare: (order) => (obj1, obj2) => {
          return dateSortCompare(obj1, obj2, order);
        },
        customBodyRender: (value) => {
          return value ? getTimeZoneDisplay(value) : '-';
        }
      }
    },
    {
      name: 'timeUntilArrival',
      label: 'Time until arrival',
      options: {
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value) => {
          if (value) {
            const days = Math.floor(value / (24 * 60 * 60));
            const hours = Math.floor((value % (24 * 60 * 60)) / 3600);
            return days > 0 ? `${days}d ${hours}h` : `${hours}h`;
          } else {
            return '0h';
          }
        }
      }
    },
    {
      name: 'positionReceived',
      label: 'Position received',
      options: {
        sort: true,
        sortThirdClickReset: true,
        customDownloadValue: (value) => value,
        sortCompare: (order) => (obj1, obj2) => {
          return dateSortCompare(obj1, obj2, order);
        },
        customBodyRender: (value) => {
          return value ? getTimeZoneDisplay(value) : '-';
        }
      }
    }
  ];

  // Filter columns based on hideColumns prop
  const filteredColumns = allColumns.map((column) => {
    let updatedColumn = { ...column };
    if (hideColumns.includes(updatedColumn.label)) {
      updatedColumn.options = {
        viewColumns: false,
        display: false,
        filter: false,
        download: false
      };
    }
    return updatedColumn;
  });

  // Determine initial sort settings
  let sortInitName = 'canalDeparture';
  let sortInitOrder = 'desc';
  tableData.forEach((row) => {
    if (Object.hasOwn(row, 'canalArrival') && Object.hasOwn(row, 'positionReceived')) {
      sortInitName = 'canalArrival';
      sortInitOrder = 'asc';
    } else if (Object.hasOwn(row, 'eta')) {
      sortInitName = 'eta';
      sortInitOrder = 'asc';
    }
  });

  const options = useMemo(
    () => ({
      selectableRows: 'none',
      responsive: 'standard',
      filter: false,
      search: false,
      download: false,
      downloadOptions: {
        filename: fileName.toLowerCase().includes('.csv') ? fileName : `${fileName}.csv`
      },
      setTableProps: () => {
        return {
          id: id || 'vessel-table'
        };
      },
      enableNestedDataAccess: '.',
      print: false,
      viewColumns: false,
      pagination: title !== 'In Transit',
      tableBodyHeight: title !== 'In Transit' ? 'auto' : '30vh',
      tableBodyMaxHeight: title !== 'In Transit' ? '80vh' : '30vh',
      toolbar: false,
      hideToolbar: title === 'In Transit',
      hideTitle: title === 'In Transit',
      hideFilter: true,
      rowsPerPageOptions: [10, 25, 50, 100],
      elevation: 0,
      sortOrder: {
        name: sortInitName,
        direction: sortInitOrder
      }
    }),
    [user, fileName, title, id, sortInitName, sortInitOrder]
  );

  return (
    <Paper id="canal-vessel-portcalls-table">
      {tableData && (
        <TableV2
          handleCustomDownload={hasExportPeriodMenu ? () => setShowModal(true) : undefined}
          data={tableData?.length ? tableData : []}
          columns={filteredColumns}
          options={options}
          loading={loading}
          title={
            <Typography id="vessel-port-calls-title" variant="h6">
              {title}
            </Typography>
          }
        />
      )}
      {showModal && (
        <DownloadDateRangeModal
          geographicalType={GEOGRAPHICAL_TYPES.CANAL}
          columns={filteredColumns}
          showModal={showModal}
          setShowModal={setShowModal}
          geoId={Number(params.canalId)}
          name={title}
        />
      )}
    </Paper>
  );
}

CanalVesselPortCallsTable.propTypes = {
  title: PropTypes.string,
  loading: PropTypes.bool,
  fileName: PropTypes.string,
  assetLevel: PropTypes.oneOf(['canal']),
  upcomingPortCalls: PropTypes.arrayOf(PropTypes.object),
  canalDeparturesData: PropTypes.arrayOf(PropTypes.object),
  hideColumns: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  hasExportPeriodMenu: PropTypes.bool
};

export default CanalVesselPortCallsTable;
